/*
 * The one path for changing the bag. The hero, the story viewer and quick view hand
 * their form to addToBag, and the picker hands Buy now to buyNow; the bag itself
 * (sections/cart-drawer.liquid, and /cart) calls cartRequest to change a quantity,
 * remove a line, wrap the order or apply a code. Either way a double tap posts once,
 * a refusal says why in Shopify's words, and every section that shows the bag -- the
 * drawer, the /cart page, the announcement bar -- comes back re-rendered inside the
 * same reply.
 *
 * Loaded once from layout/theme.liquid. Callers check for it first and let their
 * form post natively when it is missing, so a failed download never strands a shopper.
 */

// Sections that show the bag carry data-cart-section (up to five ride in one reply -- there are three).
const bagSections = () => [
  ...new Set([...document.querySelectorAll('[data-cart-section]')].map((el) => el.dataset.cartSection)),
];

// Locale-aware, so Shopify answers in the shopper's language.
const bagRoot = () => window.Shopify?.routes?.root || '/';

/*
 * The bag, told to everything that shows it: the header's count here, and `cart:updated` for
 * the rest -- the drawer, /cart and the announcement bar take their re-rendered section from
 * it, a product page its delivery block. A listener may hand back a line for the button that
 * caused it (detail.note): the announcement bar does when this add reached free delivery.
 */
function shareBag(detail) {
  if (detail.cart) {
    // Pieces, not the gift wrapping: a line in the order, but not something the shopper picked.
    const skip = Number(document.querySelector('[data-bag-skip]')?.dataset.bagSkip);
    const count = detail.cart.items.reduce((sum, item) => sum + (item.variant_id === skip ? 0 : item.quantity), 0);
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = el.dataset.cartCount.replace('*', count);
      if (el.hasAttribute('aria-hidden')) el.hidden = count === 0;
    });
  }
  document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
  return detail;
}

/*
 * Posts one change and shares the answer: Shopify renders the bag's sections into the
 * reply. Throws with Shopify's own sentence when the cart refuses, and a TypeError when
 * the request never arrived.
 *
 * `leaving` is Buy now: the page is about to be left for checkout, so no section is asked
 * for and nothing is shared -- the drawer opening over a page on its way out is a flash.
 */
window.cartRequest = async function cartRequest(url, body, source, leaving = false) {
  const sections = leaving ? [] : bagSections();
  if (sections.length) Object.assign(body, { sections: sections.join(','), sections_url: location.pathname });

  const root = bagRoot();
  const res = await fetch(root + url.replace(/^\//, ''), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const reply = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(reply.description || reply.message || '');
  if (leaving) return null;

  // A change or an update answers with the whole bag; an add only with the piece added.
  const cart = 'item_count' in reply ? reply : await fetch(`${root}cart.js`).then((r) => r.json()).catch(() => null);
  return shareBag({ cart, sections: reply.sections || {}, source, added: url.endsWith('add.js') });
};

/*
 * A page the back button restored kept the bag it was left with: an empty count in the header
 * over a drawer holding a piece, a delivery block still asking for the full amount. Read once,
 * here, and shared exactly as a change is, so everything that shows the bag catches up
 * together. `restored` tells a listener nothing was just added: the drawer stays shut, and
 * the announcement bar keeps its celebration for a real add.
 */
addEventListener('pageshow', async (event) => {
  if (!event.persisted) return;
  const ids = bagSections();
  try {
    const [cart, sections] = await Promise.all([
      fetch(`${bagRoot()}cart.js`).then((r) => r.json()),
      // This page's own address: /cart's section renders only there.
      ids.length ? fetch(`${location.pathname}?sections=${ids.join(',')}`).then((r) => r.json()) : {},
    ]);
    shareBag({ cart, sections, source: null, added: false, restored: true });
  } catch {
    // The page keeps what it showed; the next change brings it up to date.
  }
});

// The product page selector sits outside its form and uses the `form` attribute.
// form.elements finds it there; buying surfaces without a selector still add one.
function formQuantity(form) {
  const value = Number(form.elements.namedItem('quantity')?.value);
  return Number.isSafeInteger(value) && value > 0 ? value : 1;
}

/*
 * `refuse` is for a caller with somewhere better than the button to say why the cart said no
 * (the picker's stock line): it is handed Shopify's sentence, or nothing when none came.
 */
window.addToBag = async function addToBag(form, button, label, refuse) {
  label = label || button;
  if (button.disabled) return;

  const idle = label.innerHTML;
  // Disabled for the whole request: a second tap cannot post the same selection twice.
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  const settle = (shown, after) =>
    setTimeout(() => {
      button.removeAttribute('aria-busy');
      // Quick view rewrites the label itself when another size is picked; that is newer.
      if (label.textContent !== shown) return;
      label.innerHTML = idle;
      button.classList.remove('is-added');
      button.disabled = false;
    }, after);

  let detail;
  try {
    detail = await window.cartRequest(
      '/cart/add.js',
      { id: form.querySelector('[name="id"]').value, quantity: formQuantity(form) },
      button
    );
  } catch (error) {
    // Offline or blocked: the real form post still works. Only a request that never
    // arrived falls back, never one the cart answered.
    if (error instanceof TypeError) {
      form.submit();
      return;
    }
    // Sold out, or already at the stock limit. Shopify's sentence says which, in the
    // shopper's language, and nothing was added.
    if (refuse) refuse(error.message);
    else if (error.message) label.textContent = error.message;
    settle(label.textContent, !refuse && error.message ? 3000 : 0);
    return;
  }

  // Confirmed in place, then back to the priced label: a later tap is a deliberate
  // second add, not a leftover.
  button.classList.add('is-added');
  label.textContent = detail.note || button.dataset.addedLabel;
  settle(label.textContent, detail.note ? 2500 : 2000);
};

/*
 * Buy now: ensure the bag has at least the selected quantity of this variant, then checkout.
 * Going Back and pressing it again does not add the same quantity twice. `button` is the
 * form's `return_to` submitter, whose value is where checkout lives.
 */
window.buyNow = async function buyNow(form, button, refuse) {
  if (button.disabled) return;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  const release = () => {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  };
  // Back from checkout restores this page exactly as it was left: pressed.
  addEventListener('pageshow', release, { once: true });

  const id = Number(form.querySelector('[name="id"]').value);
  const quantity = formQuantity(form);
  const cart = await fetch(`${bagRoot()}cart.js`).then((r) => r.json()).catch(() => null);
  try {
    const inBag = cart?.items?.reduce((total, item) => total + (item.variant_id === id ? item.quantity : 0), 0) || 0;
    if (inBag < quantity) {
      await window.cartRequest('/cart/add.js', { id, quantity: quantity - inBag }, button, true);
    }
    location.assign(button.value);
  } catch (error) {
    // Offline or blocked: the real form post still adds the piece and opens checkout. submit()
    // names no submitter, so the button's `return_to` rides along as a field.
    if (error instanceof TypeError) {
      form.append(Object.assign(document.createElement('input'), { type: 'hidden', name: button.name, value: button.value }));
      form.submit();
      return;
    }
    release();
    refuse?.(error.message);
  }
};
